import { Link } from '@inertiajs/react'
import { FileText, Newspaper } from 'lucide-react'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import { artifactTimeLabel } from './format'
import type { RecentArtifact } from './types'

// Rótulos dos tipos de artefato (Artifact::KINDS).
const KIND_LABELS: Record<string, string> = {
  page: 'Publicação',
  dashboard: 'Painel',
  report: 'Relatório',
  proposal: 'Proposta',
  status: 'Status',
}

function kindLabel(kind: string | null): string {
  if (!kind) return 'Artefato'
  return KIND_LABELS[kind] ?? 'Artefato'
}

interface Props {
  artifacts: RecentArtifact[]
}

/**
 * "Publicações recentes" (Iteração 3, Onda A): as publicações/arquivos que os
 * colaboradores geraram (artifacts publicados), reframados. Cada linha =
 * ícone + título + "Agente · tipo" + hora, clicável para as publicações do
 * agente. Dado real. Sem artefato, some.
 */
export function RecentArtifacts({ artifacts }: Props) {
  // Coluna fixa (par de "Conversas ativas"): vazia mostra o estado em vez de
  // sumir, pra o grid de duas colunas do rodapé não colapsar.
  if (artifacts.length === 0) {
    return (
      <section aria-label="Publicações recentes" className="space-y-2">
        <SectionLabel>Publicações recentes</SectionLabel>
        <PanelEmptyState icon={Newspaper} title="Nada publicado ainda" description="Aparece aqui quando sair do forno." />
      </section>
    )
  }

  return (
    <section aria-label="Publicações recentes" className="space-y-2">
      <SectionLabel>Publicações recentes</SectionLabel>
      <ul className="overflow-hidden rounded-xl border border-border bg-card">
        {artifacts.map((artifact) => (
          <li key={artifact.id}>
            <Link
              href={artifact.path}
              className="flex items-center gap-3 border-b border-border px-4 py-[11px] transition-colors duration-150 last:border-b-0 hover:bg-accent"
            >
              <span className="flex size-8 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand">
                <FileText className="size-4" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-medium text-foreground">{artifact.title}</p>
                <p className="truncate text-[11.5px] text-faint-foreground">
                  {artifact.worker_name} · {kindLabel(artifact.kind)}
                </p>
              </div>
              <span className="flex-none font-mono text-[11px] text-faint-foreground">
                {artifactTimeLabel(artifact.published_at)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
